/**
 * Itinerary Builder Page
 * 
 * NOTE: Routes are now drawn CLIENT-SIDE using Mapbox GL JS, not fetched from the backend.
 * The backend only manages waypoints. Route geometry is calculated in the browser by:
 * 1. Fetching waypoints for a trip from the backend
 * 2. Using Mapbox GL JS Directions API to draw routes between waypoints
 * 3. Rendering route lines on the map using Mapbox GL JS layers
 * 
 * This approach:
 * - Reduces backend complexity (no route generation queue, no route storage)
 * - Improves performance (routes drawn instantly, no waiting for backend)
 * - Enables real-time route updates as waypoints are added/moved
 * - Leverages Mapbox's powerful client-side routing capabilities
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, X, MapPin, TreePine, PanelRightClose, PanelLeftClose } from '@/ui/icons'
import { useTrips } from '@/modules/trips'
import { useItineraryBuilder } from './itinerary-builder/hooks/useItineraryBuilder'
import { TripsListPanel, ItineraryView, CampsiteSearchDialog, LibraryWaypointsToggle, PublicLandsToggle, DispersedCampFinderToggle, AddWaypointToTripPanel, WaypointDetailsPanel } from './itinerary-builder/components'
import { organizeWaypointsByDay } from './itinerary-builder/utils/itineraryHelpers'
import { MapContainer } from './map/components/MapContainer'
import type { MenuItem } from './map/components/SideMenuBar'
import { ExplorePanel } from './map/components/ExplorePanel'
import { LibraryPanel } from './map/components/LibraryPanel'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/components'
import { MapDataProvider, useMapDataContext } from '@/modules/routes/contexts/MapDataContext'
import { MapPageProvider, useMapPageContext } from '@/modules/routes/contexts/MapPageContext'
import { RoutesApi } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'
import { useUserLocation } from '@/modules/routes/hooks/useUserLocation'
import { useLocationSearch } from '@/modules/routes/hooks/useLocationSearch'
import { useWaypointActions } from '@/modules/routes/hooks/useWaypointActions'
import { useWaypointFolders } from '@/modules/routes/hooks/useWaypointFolders'
import { usePOIHandlers } from '@/modules/routes/hooks/usePOIHandlers'
import { calculatePointBufferBbox } from '@/modules/routes/utils/routeBufferPolygon'
import { POIDiscoveryPanel } from './itinerary-builder/components/panels'
import { UpgradePrompt, useSubscription } from '@/modules/subscriptions'
import { PageLayout } from '@/ui/layout'
import type { GeoJSON } from 'geojson'

const EXPLORE_SIDEBAR_EXPANDED_WIDTH_PX = 288
const EXPLORE_SIDEBAR_COLLAPSED_WIDTH_PX = 56
const EXPLORE_LEFT_PANEL_STORAGE_KEY = 'sitrepp.explore.leftPanelCollapsed'

function ItineraryBuilderPageContent() {
  const { trips, isLoading } = useTrips()
  const { updateWaypoints, updateRoutes } = useMapDataContext()
  const { dialogs, setShowPOISearch } = useMapPageContext()
  const { getAccessToken } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  
  // State declarations (must come before useItineraryBuilder since it uses libraryWaypoints)
  const [libraryWaypoints, setLibraryWaypoints] = useState<any[]>([])
  const [libraryRefreshTrigger, setLibraryRefreshTrigger] = useState(0)
  // Check URL to determine active menu
  const isTripsRoute = location.pathname === '/explore/trips'
  const isExploreRoute = location.pathname === '/explore' || location.pathname === '/explore/'
  // Check if viewing a specific trip (URL pattern: /explore/trip/:tripId)
  const isTripViewRoute = location.pathname.startsWith('/explore/trip/')
  // Check if viewing a waypoint (URL pattern: /explore/waypoint/:waypointId)
  const isWaypointViewRoute = location.pathname.startsWith('/explore/waypoint/')
  const initialMenu: MenuItem | null = isWaypointViewRoute ? 'library' : isTripsRoute || isTripViewRoute ? 'trips' : isExploreRoute ? 'library' : null
  const [activeMenu, setActiveMenu] = useState<MenuItem | null>(initialMenu)
  const [isAddingStop, setIsAddingStop] = useState(false)
  const [deadEnds, setDeadEnds] = useState<any[]>([])
  const [candidates, setCandidates] = useState<any[]>([])
  const [isLoadingDeadEnds, setIsLoadingDeadEnds] = useState(false)
  const [deadEndError, setDeadEndError] = useState<string | null>(null)
  const [forestRoadsError, setForestRoadsError] = useState<string | null>(null)
  const [exploreSearchError, setExploreSearchError] = useState<string | null>(null)
  const [discoverySource, setDiscoverySource] = useState<'cache' | 'overlapping' | 'queued' | 'live' | null>(null)
  const [discoveryQueuedAt, setDiscoveryQueuedAt] = useState<string | null>(null)
  const [searchRadiusMiles, setSearchRadiusMiles] = useState<number | null>(null)
  const [searchRouteGeometry, setSearchRouteGeometry] = useState<GeoJSON.LineString | GeoJSON.MultiLineString | null>(null)
  const [searchBboxVisualization, setSearchBboxVisualization] = useState<{
    bbox: [number, number, number, number]
    bboxArea?: number
    bboxSize?: { width: number; height: number }
    bboxCenter?: [number, number]
    searchCenter?: [number, number]
    searchRadiusMiles?: number
  } | null>(null)
  const [showCampsiteDialog, setShowCampsiteDialog] = useState(false)
  const [showLibraryWaypoints, setShowLibraryWaypoints] = useState(false)
  const [showPublicLands, setShowPublicLands] = useState(false)
  const [isLoadingPublicLands, setIsLoadingPublicLands] = useState(false)
  const [selectedWaypointForAdd, setSelectedWaypointForAdd] = useState<any | null>(null)
  const [centerOnPOI, setCenterOnPOI] = useState<{ latitude: number; longitude: number } | null>(null)
  const [centerOnDeadEnd, setCenterOnDeadEnd] = useState<{ latitude: number; longitude: number } | null>(null)
  const [showPOIDiscovery, setShowPOIDiscovery] = useState(false)
  const [isDeadEndClickSearchMode, setIsDeadEndClickSearchMode] = useState(false)
  const [deadEndUpgradeRequired, setDeadEndUpgradeRequired] = useState(false)
  const [showDispersedCampsites, setShowDispersedCampsites] = useState(false)
  const [dispersedCampsitesLoading, setDispersedCampsitesLoading] = useState(false)
  const [campFinderUpgradeRequired, setCampFinderUpgradeRequired] = useState(false)
  const { status: subscriptionStatus } = useSubscription()
  const canUseCampFinder = subscriptionStatus?.features?.canUseDispersedCampingFinder ?? false
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(EXPLORE_LEFT_PANEL_STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })
  const [libraryFolderFocusWaypoints, setLibraryFolderFocusWaypoints] = useState<any[] | null>(null)
  const folderFocusTimeoutRef = useRef<number | null>(null)
  const isPanelOpen = activeMenu === 'explore' || activeMenu === 'trips' || activeMenu === 'library'

  const {
    selectedTrip,
    viewMode,
    mapClickCoordinates,
    waypoints,
    waypointsLoading,
    route,
    routeSegments,
    routesLoading,
    selectedWaypoint,
    setSelectedWaypoint,
    centerOnWaypoint,
    setCenterOnWaypoint,
    handleWaypointClick: originalHandleWaypointClick,
    handleTripSelect,
    handleBackToTrips,
    handleMapClick,
    clearMapClick,
    handleAddStop,
    handleReorderWaypoints,
    handleDeleteWaypoint,
    handleUpdateWaypointDay,
    handleUpdateWaypoint,
    handleAddWaypointToDay,
    fetchWaypoints,
    waypointActions: itineraryWaypointActions
  } = useItineraryBuilder({ libraryWaypoints })

  // Wrap handleWaypointClick - library waypoints just center the map, don't open Add to Trip panel
  // Users must explicitly click "Add to Trip" button to add waypoints
  const handleWaypointClick = useCallback((waypoint: any) => {
    // For all waypoints (library or trip), just use the original handler
    // This will center the map on the waypoint and set it as selected
    originalHandleWaypointClick(waypoint)
  }, [originalHandleWaypointClick])

  // Handle waypoint selection from itinerary list - centers map and shows details
  const handleWaypointSelectFromList = useCallback((waypoint: any) => {
    setSelectedWaypoint(waypoint)
    setCenterOnWaypoint(waypoint)
  }, [setSelectedWaypoint, setCenterOnWaypoint])

  // Handle closing waypoint details panel
  const handleCloseWaypointDetails = useCallback(() => {
    setSelectedWaypoint(null)
    setCenterOnWaypoint(null)
    // Navigate away from waypoint URL to prevent re-selection
    if (isWaypointViewRoute) {
      // If we're in library view, navigate back to explore (library panel)
      if (activeMenu === 'library') {
        navigate('/explore', { replace: true })
      } else if (selectedTrip) {
        // If we're viewing a trip, navigate back to trip view
        navigate(`/explore/trip/${selectedTrip.id}`, { replace: true })
      } else {
        // Otherwise navigate back to base itinerary builder
        navigate('/explore', { replace: true })
      }
    }
  }, [setSelectedWaypoint, setCenterOnWaypoint, isWaypointViewRoute, activeMenu, selectedTrip, navigate])

  // User location for map centering
  const userLocation = useUserLocation()

  // Location search for search bar
  const locationSearch = useLocationSearch()

  const isExploreAppRoute = useMemo(
    () => location.pathname.startsWith('/explore'),
    [location.pathname]
  )
  const mapSearchCoordinatesOnly = isExploreAppRoute && !selectedTrip
  const showMapLocationSearch = isExploreAppRoute || Boolean(selectedTrip)

  useEffect(() => {
    if (!mapSearchCoordinatesOnly) setExploreSearchError(null)
  }, [mapSearchCoordinatesOnly])

  // Use waypointActions from useItineraryBuilder to ensure optimistic updates work correctly
  // This ensures the onWaypointCreated callback in useItineraryBuilder is triggered
  const waypointActions = itineraryWaypointActions || useWaypointActions({
    tripId: selectedTrip?.id || null,
    routeId: null,
    fetchWaypoints: async () => {
      // Return current waypoints - the parent hook will handle refreshing
      return waypoints || []
    },
    onWaypointCreated: async (newWaypoint) => {
      // Fallback: Trigger waypoint refresh if waypointActions from useItineraryBuilder is not available
      if (selectedTrip?.id) {
        await fetchWaypoints()
      }
    }
  })

  const routesApi = useMemo(() => new RoutesApi(getAccessToken), [getAccessToken])
  const { folders, refreshFolders } = useWaypointFolders({ getAccessToken })
  
  // POI handlers - use first route segment ID if available, otherwise use main route ID
  const effectiveRouteId = routeSegments && routeSegments.length > 0 
    ? routeSegments[0].id 
    : route?.id || null
  
  // Extract tripId and tripDay from routeSegments for trip-centric POI search
  const effectiveTripId = selectedTrip?.id || null
  const effectiveTripDay = routeSegments && routeSegments.length > 0 && routeSegments[0].day !== undefined
    ? routeSegments[0].day
    : undefined

  const poiHandlers = usePOIHandlers({
    routeId: effectiveRouteId,
    tripId: effectiveTripId,
    getWaypointsByTripAndRoute: async (tripId: string, routeId: string) => {
      // Return waypoints for the trip/route if available
      return (waypoints || []).filter(wp => wp.trip_id === tripId)
    },
    fetchWaypoints: async (params?: any) => {
      if (selectedTrip?.id && fetchWaypoints) {
        await fetchWaypoints()
      }
      return waypoints || []
    },
    onPOISaved: async () => {
      // Refresh waypoints after POI is saved
      if (selectedTrip?.id && fetchWaypoints) {
        await fetchWaypoints()
      }
    }
  })

  // Handle POI click - center map on POI
  const handlePOIClick = useCallback((poi: any) => {
    if (poi.latitude && poi.longitude) {
      setCenterOnPOI({ latitude: poi.latitude, longitude: poi.longitude })
    }
    poiHandlers.handlePOISelect(poi)
  }, [poiHandlers])

  // Handle adding POI to trip
  const handleAddPOIToTrip = useCallback(async (poi: any) => {
    if (!selectedTrip?.id) {

      return
    }
    await poiHandlers.handleSavePOIToTrip(poi, selectedTrip.id)
  }, [poiHandlers, selectedTrip])

  // Handle adding dead end to trip as POI
  const handleAddDeadEndToTrip = useCallback(async (deadEnd: any) => {
    if (!selectedTrip?.id) {

      return
    }
    try {
      const [lng, lat] = deadEnd.coordinates
      const waypointData = {
        trip_id: selectedTrip.id,
        name: deadEnd.roadName || 'Potential Campsite',
        latitude: lat,
        longitude: lng,
        notes: `Potential campsite - ${deadEnd.roadName || 'Unnamed road'}`,
        poi_type: 'campsite' as const,
        source: 'campsite_discovery' as const,
        is_poi: true
      }
      await routesApi.createWaypoint(waypointData)

      if (fetchWaypoints) {
        await fetchWaypoints()
      }
    } catch (error) {

    }
  }, [selectedTrip, routesApi, fetchWaypoints])
  
  // Request cancellation (for aborting in-flight requests)
  const abortControllerRef = useRef<AbortController | null>(null)
  
  // Track paste events to prevent double-searching
  const pasteHandledRef = useRef(false)
  
  // Find manually added folder (system folder with system_type = 'manual')
  const manuallyAddedFolder = useMemo(() => {
    const findManualFolder = (folderList: typeof folders): typeof folders[0] | null => {
      for (const folder of folderList) {
        if (folder.is_system && folder.system_type === 'manual') {
          return folder
        }
        if (folder.children && folder.children.length > 0) {
          const found = findManualFolder(folder.children)
          if (found) return found
        }
      }
      return null
    }
    return findManualFolder(folders)
  }, [folders])

  // Request user location on mount
  useEffect(() => {
    if (!userLocation.location && !userLocation.approximateLocation && !userLocation.isLoading) {
      userLocation.requestLocation()
    }
  }, [userLocation])

  // Fetch standalone waypoints (null trip_id) that are GPX imported
  const isFetchingLibraryWaypointsRef = useRef(false)
  const fetchLibraryWaypoints = useCallback(async () => {
    // Prevent duplicate fetches
    if (isFetchingLibraryWaypointsRef.current) {
      return
    }
    
    try {
      isFetchingLibraryWaypointsRef.current = true
      // Fetch all user waypoints and filter for library waypoints
      const allWaypoints = await routesApi.getWaypoints({})
      // Filter for library waypoints (folder_id IS NOT NULL)
      // Includes GPX imported, manually added, and trip folder waypoints (waypoints in trip folders have folder_id set)
      const libraryWps = allWaypoints.filter(
        wp => wp.folder_id !== null && wp.folder_id !== undefined
      )
      setLibraryWaypoints(libraryWps)
    } catch (error) {
      setLibraryWaypoints([])
    } finally {
      isFetchingLibraryWaypointsRef.current = false
    }
  }, [routesApi])

  // Only fetch library waypoints once on mount, not on every routesApi change
  // RoutesApi should be stable via useMemo
  useEffect(() => {
    fetchLibraryWaypoints()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Sync activeMenu with URL
  useEffect(() => {
    if (isExploreRoute && activeMenu !== 'library') {
      setActiveMenu('library')
      setShowLibraryWaypoints(true)
    } else if (isWaypointViewRoute) {
      // When viewing a waypoint, determine which menu to keep open based on waypoint source
      // If waypoint has no trip_id, it's from library; otherwise it's from a trip
      const waypointIsFromLibrary = selectedWaypoint && !selectedWaypoint.trip_id
      const waypointIsFromTrip = selectedWaypoint && selectedWaypoint.trip_id
      
      if (waypointIsFromLibrary && activeMenu !== 'library') {
        // Keep library menu open for library waypoints
        setActiveMenu('library')
        setShowLibraryWaypoints(true)
      } else if (waypointIsFromTrip && activeMenu !== 'trips') {
        // Keep trips menu open for trip waypoints
        setActiveMenu('trips')
      } else if (!selectedWaypoint) {
        // If waypoint not loaded yet, preserve current menu state
        // Don't change activeMenu
      }
    } else if ((isTripsRoute || isTripViewRoute) && activeMenu !== 'trips') {
      // Keep trips menu open when viewing trips list OR a specific trip
      setActiveMenu('trips')
    } else if (!isTripsRoute && !isExploreRoute && !isTripViewRoute && !isWaypointViewRoute) {
      // If not on any specific explore route, clear menu if it was set
      if (activeMenu === 'library') {
        setActiveMenu(null)
        setShowLibraryWaypoints(false)
      } else if (activeMenu === 'trips' || activeMenu === 'explore') {
        setActiveMenu(null)
      }
    }
  }, [isTripsRoute, isExploreRoute, isTripViewRoute, isWaypointViewRoute, activeMenu, selectedWaypoint])

  useEffect(() => {
    try {
      window.localStorage.setItem(EXPLORE_LEFT_PANEL_STORAGE_KEY, String(leftPanelCollapsed))
    } catch {
      // ignore persistence errors
    }
  }, [leftPanelCollapsed])

  useEffect(() => {
    return () => {
      if (folderFocusTimeoutRef.current !== null) {
        window.clearTimeout(folderFocusTimeoutRef.current)
      }
    }
  }, [])

  // Auto-enable library waypoints when library menu is active
  useEffect(() => {
    if (activeMenu === 'library') {
      setShowLibraryWaypoints(true)
    }
  }, [activeMenu])

  useEffect(() => {
    if (activeMenu !== 'library' && activeMenu !== 'explore') {
      setLibraryFolderFocusWaypoints(null)
    }
  }, [activeMenu])

  const handleFolderWaypointsFocus = useCallback((folderWaypoints: any[]) => {
    const validFolderWaypoints = folderWaypoints.filter(
      (waypoint) =>
        typeof waypoint.latitude === 'number' &&
        typeof waypoint.longitude === 'number' &&
        !Number.isNaN(waypoint.latitude) &&
        !Number.isNaN(waypoint.longitude)
    )

    if (folderFocusTimeoutRef.current !== null) {
      window.clearTimeout(folderFocusTimeoutRef.current)
    }

    // Reset first so repeated clicks on the same folder still trigger map recenter.
    setLibraryFolderFocusWaypoints(null)
    folderFocusTimeoutRef.current = window.setTimeout(() => {
      setLibraryFolderFocusWaypoints(validFolderWaypoints)
    }, 0)
  }, [])

  // Update MapDataContext with waypoints
  // When in library view, only show library waypoints (no trip waypoints)
  // When a trip is selected, only include waypoints for that trip
  // Library waypoints are controlled by the toggle
  useEffect(() => {
    let waypointsToShow: any[] = []
    
    // If library menu is active, only show library waypoints (no trip waypoints)
    if (activeMenu === 'library') {
      waypointsToShow = [...libraryWaypoints]
    } else {
      // Normal trip view
      waypointsToShow = [...(waypoints || [])]
      
      // If a trip is selected, filter to only show waypoints for that trip
      if (selectedTrip?.id) {
        waypointsToShow = waypointsToShow.filter(wp => wp.trip_id === selectedTrip.id)
      }
      
      // Add library waypoints if toggle is enabled
      if (showLibraryWaypoints) {
        waypointsToShow = [...waypointsToShow, ...libraryWaypoints]
      }
    }
    
    updateWaypoints(waypointsToShow)
  }, [waypoints, libraryWaypoints, selectedTrip?.id, showLibraryWaypoints, activeMenu, updateWaypoints])

  // Center map on waypoints when a trip is selected and waypoints are loaded
  // Only center if there are waypoints and no route segments (routes take priority)
  const tripCenterOnWaypoints = useMemo(() => {
    // Only center on waypoints if:
    // 1. A trip is selected
    // 2. Waypoints are loaded (not loading)
    // 3. There are waypoints for this trip
    // 4. We're not in library view
    // 5. No route segments exist (routes take priority for centering)
    if (
      selectedTrip?.id &&
      !waypointsLoading &&
      waypoints &&
      waypoints.length > 0 &&
      activeMenu !== 'library' &&
      (!routeSegments || routeSegments.length === 0)
    ) {
      // Filter to only waypoints for this trip with valid coordinates
      // Note: waypoints from useItineraryBuilder should already be filtered to this trip,
      // but they might not have trip_id set, so we include all waypoints if trip_id matches or is missing
      const tripWaypoints = waypoints
        .filter(wp => {
          // Include waypoint if it has trip_id matching selected trip, or if trip_id is missing
          // (waypoints from useItineraryBuilder should all belong to the selected trip)
          return wp.trip_id === selectedTrip.id || (!wp.trip_id && selectedTrip.id)
        })
        .filter(wp => wp.latitude != null && wp.longitude != null)
      
      if (tripWaypoints.length > 0) {
        return tripWaypoints
      }
    }
    return null
  }, [selectedTrip?.id, waypoints, waypointsLoading, activeMenu, routeSegments])

  const centerOnWaypoints = useMemo(() => {
    if ((activeMenu === 'library' || activeMenu === 'explore') && libraryFolderFocusWaypoints) {
      return libraryFolderFocusWaypoints
    }
    return tripCenterOnWaypoints
  }, [activeMenu, libraryFolderFocusWaypoints, tripCenterOnWaypoints])

  useEffect(() => {
    updateRoutes(routeSegments || [])
  }, [routeSegments, updateRoutes])

  // Clear campsite search state when trip changes or when switching to library view
  // Also close POI discovery panel when switching away from trips menu
  useEffect(() => {
    setDeadEnds([])
    setCandidates([])
    setSearchRouteGeometry(null)
    setSearchRadiusMiles(null)
    setSearchBboxVisualization(null)
    setShowCampsiteDialog(false)
    // Close POI discovery panel when switching away from trips menu
    if (activeMenu !== 'trips') {
      setShowPOIDiscovery(false)
    }
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }, [selectedTrip?.id, activeMenu])

  const handleMenuClick = useCallback((menu: MenuItem) => {
    if (menu === 'library') {
      // Navigate to library URL
      if (activeMenu === 'library') {
        // If already on library, navigate back
        navigate('/explore', { replace: true })
        setActiveMenu(null)
        setShowLibraryWaypoints(false)
        // Clear map state
        setSelectedWaypoint(null)
        setSelectedWaypointForAdd(null)
        clearMapClick()
        // Clear waypoints and routes from map
        updateWaypoints([])
        updateRoutes([])
      } else {
        // Navigate to explore (library panel)
        navigate('/explore', { replace: true })
        setActiveMenu('library')
        setShowLibraryWaypoints(true)
      }
    } else if (menu === 'trips') {
      // Navigate to trips URL
      if (activeMenu === 'trips') {
        // If already on trips, navigate back
        navigate('/explore', { replace: true })
        setActiveMenu(null)
        // Clear map state
        setSelectedWaypoint(null)
        setSelectedWaypointForAdd(null)
        clearMapClick()
        // Clear waypoints and routes from map
        updateWaypoints([])
        updateRoutes([])
      } else {
        // Navigate to trips
        navigate('/explore/trips', { replace: true })
        setActiveMenu('trips')
      }
    } else if (menu === 'explore') {
      // Navigate to explore URL
      if (activeMenu === 'explore') {
        // If already on explore, navigate back
        navigate('/explore', { replace: true })
        setActiveMenu(null)
        // Clear map state
        setSelectedWaypoint(null)
        setSelectedWaypointForAdd(null)
        clearMapClick()
        // Clear waypoints and routes from map
        updateWaypoints([])
        updateRoutes([])
      } else {
        // Navigate to explore - no trip dependencies
        navigate('/explore', { replace: true })
        setActiveMenu('explore')
      }
    }
  }, [activeMenu, navigate, clearMapClick, updateWaypoints, updateRoutes, selectedTrip])

  const handleClosePanel = useCallback(() => {
    if (activeMenu === 'library') {
      // Navigate back when closing library
      navigate('/explore', { replace: true })
      setShowLibraryWaypoints(false)
      // Clear map state
      setSelectedWaypoint(null)
      setSelectedWaypointForAdd(null)
      clearMapClick()
      // Clear waypoints and routes from map
      updateWaypoints([])
      updateRoutes([])
    } else if (activeMenu === 'trips' || activeMenu === 'explore') {
      // Navigate back when closing trips or explore
      navigate('/explore', { replace: true })
      // Clear map state
      setSelectedWaypoint(null)
      setSelectedWaypointForAdd(null)
      clearMapClick()
      // Clear waypoints and routes from map
      updateWaypoints([])
      updateRoutes([])
    }
    setActiveMenu(null)
  }, [activeMenu, navigate, clearMapClick, updateWaypoints, updateRoutes])

  // Helper to get route geometry based on day or routeId selection
  const getRouteGeometryForSearch = useCallback((day?: number, routeId?: string) => {
    // If routeId is provided, find that specific route
    if (routeId && routeSegments) {
      const selectedRoute = routeSegments.find(r => r.id === routeId)
      if (selectedRoute?.geometry) {
        const geom = selectedRoute.geometry
        if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
          return geom as GeoJSON.LineString | GeoJSON.MultiLineString
        }
      }
    }
    
    // If day is provided, find route for that day
    if (day !== undefined && routeSegments) {
      const dayRoute = routeSegments.find(r => r.day === day)
      if (dayRoute?.geometry) {
        const geom = dayRoute.geometry
        if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
          return geom as GeoJSON.LineString | GeoJSON.MultiLineString
        }
      }
    }
    
    // Fallback: Use first route segment (to avoid searching far-apart routes)
    if (routeSegments && routeSegments.length > 0) {
      const firstRoute = routeSegments[0]
      if (firstRoute?.geometry) {
        const geom = firstRoute.geometry
        if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
          return geom as GeoJSON.LineString | GeoJSON.MultiLineString
        }
      }
    }
    
    // Final fallback to main route
    if (route?.geometry) {
      const geom = route.geometry
      if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
        return geom as GeoJSON.LineString | GeoJSON.MultiLineString
      }
    }
    
    return null
  }, [route, routeSegments])

  // Helper to find waypoints with poi_type = 'campsite'
  const findCampsiteWaypoints = useCallback(() => {
    return waypoints?.filter(
      wp => wp.poi_type === 'campsite' && 
            wp.latitude != null && 
            wp.longitude != null
    ) || []
  }, [waypoints])

  // Route-based campsite discovery handler
  // Handles campsite search with different modes (day, route, waypoint)
  // Backend handles rate limiting and caching
  // options.silent: background poll, no loading state, no state clear (for "Check for results")
  const handleFindCampsites = useCallback(async (selection?: { day?: number; routeId?: string; waypointId?: string }, options?: { silent?: boolean }) => {
    const day = selection?.day
    const routeId = selection?.routeId
    const waypointId = selection?.waypointId
    const silent = options?.silent ?? false
    
    // Determine radius based on search type
    // Waypoint searches use smaller radius (2.5 miles) for faster queries
    // Route/day searches use 5 miles
    const radiusMiles = waypointId ? 2.5 : 5
    
    if (!silent) {
      // Cancel any pending request (user-initiated search)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Prevent multiple simultaneous user-initiated requests
      if (isLoadingDeadEnds) {
        return
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      setIsLoadingDeadEnds(true)
      setDeadEnds([])
      setCandidates([])
      setDeadEndError(null)
      setDiscoverySource(null)
      setDiscoveryQueuedAt(null)
      setSearchBboxVisualization(null)
    } else {
      // Silent poll: don't touch abort controller or loading state
    }

    // Silent polls use a no-op controller; user-initiated uses real AbortController
    const abortController = silent
      ? ({ signal: { aborted: false } } as AbortController)
      : (abortControllerRef.current as AbortController)

    try {
      // Handle waypoint-based search
      if (waypointId) {
        // Find the waypoint to get its coordinates
        const waypoint = waypoints?.find(wp => wp.id === waypointId)
        
        if (!waypoint) {

          return
        }
        
        if (!waypoint.longitude || !waypoint.latitude) {

          return
        }
        
        // Show loading notification

        try {
          const response = await routesApi.findDeadEnds(undefined, {
            waypointId,
            radiusMiles,
            limit: 25
          })
          
          // Check if request was aborted
          if (abortController.signal.aborted) {
            return
          }
          
          // Combine deadEnds with candidates
          const allDeadEnds = [
            ...(response.deadEnds || []),
            ...(response.candidates || []).map(candidate => ({
              coordinates: candidate.coordinates,
              roadId: candidate.roadId,
              roadName: candidate.roadName,
              distanceFromMainRoad: candidate.distanceFromMainRoad,
              classification: 'dead_end' as const,
              metadata: candidate.metadata,
              candidate
            }))
          ]
          setDeadEnds(allDeadEnds)
          setCandidates(response.candidates || [])
          setDiscoverySource(response.source ?? null)
          setDiscoveryQueuedAt(response.queuedAt ?? null)
          setSearchRouteGeometry({
            type: 'LineString',
            coordinates: [[waypoint.longitude, waypoint.latitude], [waypoint.longitude, waypoint.latitude]]
          } as GeoJSON.LineString)
          setSearchRadiusMiles(radiusMiles)
          if (response.visualization) setSearchBboxVisualization(response.visualization)
        } catch (error: any) {
          // Ignore abort errors
          if (error?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
            return
          }
          const raw = error?.response?.data?.error || error?.message || 'Failed to find campsites'
          const isOverloadOrTimeout = typeof raw === 'string' && (raw.includes('overloaded') || raw.includes('timeout') || raw.includes('too busy'))
          setDeadEndError(isOverloadOrTimeout ? 'OpenStreetMap is busy. Try again in a moment or use a smaller area.' : raw)
          if (error?.response?.data?.visualization) setSearchBboxVisualization(error.response.data.visualization)
          else setSearchBboxVisualization(null)
          setDeadEnds([])
          setCandidates([])
          setSearchRouteGeometry(null)
          setSearchRadiusMiles(null)
        }
        return
      }
      
      // If no waypointId provided, find campsite waypoints
      const campsiteWaypoints = findCampsiteWaypoints()
      
      if (campsiteWaypoints.length > 0) {
        // Use first campsite waypoint
        const campsiteWaypoint = campsiteWaypoints[0]
        
        // Show loading notification

        try {
          const response = await routesApi.findDeadEnds(undefined, {
            waypointId: campsiteWaypoint.id,
            radiusMiles: 2.5, // Use 2.5 miles for waypoint searches
            limit: 25
          })
          
          // Check if request was aborted
          if (abortController.signal.aborted) {
            return
          }
          
          // Combine deadEnds with candidates
          const allDeadEnds = [
            ...(response.deadEnds || []),
            ...(response.candidates || []).map(candidate => ({
              coordinates: candidate.coordinates,
              roadId: candidate.roadId,
              roadName: candidate.roadName,
              distanceFromMainRoad: candidate.distanceFromMainRoad,
              classification: 'dead_end' as const,
              metadata: candidate.metadata,
              candidate
            }))
          ]
          setDeadEnds(allDeadEnds)
          setCandidates(response.candidates || [])
          setDiscoverySource(response.source ?? null)
          setDiscoveryQueuedAt(response.queuedAt ?? null)
          setSearchRouteGeometry({
            type: 'LineString',
            coordinates: [[campsiteWaypoint.longitude!, campsiteWaypoint.latitude!], [campsiteWaypoint.longitude!, campsiteWaypoint.latitude!]]
          } as GeoJSON.LineString)
          setSearchRadiusMiles(2.5)
          if (response.visualization) setSearchBboxVisualization(response.visualization)
        } catch (error: any) {
          if (error?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) return
          const raw = error?.response?.data?.error || error?.message || 'Failed to find campsites'
          const isOverloadOrTimeout = typeof raw === 'string' && (raw.includes('overloaded') || raw.includes('timeout') || raw.includes('too busy'))
          setDeadEndError(isOverloadOrTimeout ? 'OpenStreetMap is busy. Try again in a moment or use a smaller area.' : raw)
          if (error?.response?.data?.visualization) setSearchBboxVisualization(error.response.data.visualization)
          else setSearchBboxVisualization(null)
          setDeadEnds([])
          setCandidates([])
          setSearchRouteGeometry(null)
          setSearchRadiusMiles(null)
        }
        return
      }
      
      // Fallback to route/day-based search if no campsite waypoints found
      const routeGeometry = getRouteGeometryForSearch(day, routeId)

      if (!routeGeometry) {

        return
      }

      // Show loading notification
      const searchType = day !== undefined ? `Day ${day}` : routeId ? 'route' : 'area'

      try {
        const response = await routesApi.findDeadEnds(undefined, {
          routeGeometry,
          radiusMiles,
          limit: 25
        })

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return
        }
        

        // Combine deadEnds with candidates
        const allDeadEnds = [
          ...(response.deadEnds || []),
          ...(response.candidates || []).map(candidate => ({
            coordinates: candidate.coordinates,
            roadId: candidate.roadId,
            roadName: candidate.roadName,
            distanceFromMainRoad: candidate.distanceFromMainRoad,
            classification: 'dead_end' as const,
            metadata: candidate.metadata,
            candidate
          }))
        ]
        setDeadEnds(allDeadEnds)
        setCandidates(response.candidates || [])
        setDiscoverySource(response.source ?? null)
        setDiscoveryQueuedAt(response.queuedAt ?? null)
        setSearchRouteGeometry(routeGeometry)
        setSearchRadiusMiles(5)
        if (response.visualization) setSearchBboxVisualization(response.visualization)
      } catch (error: any) {
        if (error?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) return
        const raw = error?.response?.data?.error || error?.message || 'Failed to find campsites'
        const isOverloadOrTimeout = typeof raw === 'string' && (raw.includes('overloaded') || raw.includes('timeout') || raw.includes('too busy'))
        setDeadEndError(isOverloadOrTimeout ? 'OpenStreetMap is busy. Try again in a moment or use a smaller area.' : raw)
        if (error?.response?.data?.visualization) setSearchBboxVisualization(error.response.data.visualization)
        else setSearchBboxVisualization(null)
        setDeadEnds([])
        setCandidates([])
        setSearchRouteGeometry(null)
        setSearchRadiusMiles(null)
      }
    } finally {
      setIsLoadingDeadEnds(false)
      abortControllerRef.current = null
    }
  }, [route, routeSegments, routesApi, getRouteGeometryForSearch, isLoadingDeadEnds, waypoints, findCampsiteWaypoints])

  // Handler to open campsite search dialog (now accessed via POIDiscoveryPanel)
  const handleOpenCampsiteSearch = useCallback(() => {
    setShowCampsiteDialog(true)
  }, [])

  // Handler for dialog search
  const handleDialogSearch = useCallback((selection: { day?: number; routeId?: string; waypointId?: string }) => {
    setShowCampsiteDialog(false)
    handleFindCampsites(selection)
  }, [handleFindCampsites])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const handleAddStopClick = useCallback(async () => {
    setIsAddingStop(true)
    try {
      await handleAddStop()
    } finally {
      setIsAddingStop(false)
    }
  }, [handleAddStop])

  // Handler for saving search result as waypoint
  const handleSaveSearchResultAsWaypoint = useCallback(async () => {
    const waypointData = locationSearch.getWaypointData()
    if (!waypointData) return

    if (!selectedTrip) {

      return
    }

    try {
      // Use waypointActions to save the search result as a waypoint
      // This will create the waypoint and trigger a refresh
      await waypointActions.handleSaveSearchResultAsWaypoint(waypointData)
      locationSearch.onClearResult()
      locationSearch.setSearchQuery('')
      // Note: Waypoints will be refreshed automatically by useItineraryBuilder's useTripWaypoints hook

    } catch (error) {
      // Error already handled in waypointActions
    }
  }, [locationSearch, waypointActions, selectedTrip])

  // Handler for adding search result to timeline (same as saving as waypoint in itinerary builder)
  const handleAddSearchResultToTimeline = useCallback(async () => {
    // In itinerary builder, adding to timeline means adding as waypoint
    await handleSaveSearchResultAsWaypoint()
  }, [handleSaveSearchResultAsWaypoint])

  // Handler for adding waypoint to library from map click
  const handleAddToLibraryFromMap = useCallback(async () => {
    if (!mapClickCoordinates) return

    try {
      // Get folder ID (use manually added folder if available, otherwise null - backend will handle)
      const folderId = manuallyAddedFolder?.id || null
      
      // Create waypoint with folder_id to add to library
      const waypointData = {
        name: `Waypoint ${new Date().toLocaleDateString()}`,
        latitude: mapClickCoordinates.latitude,
        longitude: mapClickCoordinates.longitude,
        folder_id: folderId,
        trip_id: null, // Library waypoints don't belong to a trip
        route_id: null,
        poi_type: null, // Default to null (no label) - user can choose their label
        source: 'manual' as const
      }

      await routesApi.createWaypoint(waypointData)

      // Refresh library waypoints in both parent and LibraryPanel
      await fetchLibraryWaypoints()
      // Trigger refresh in LibraryPanel component
      setLibraryRefreshTrigger(prev => prev + 1)
      
      // Clear map click coordinates
      clearMapClick()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add waypoint to library'

    }
  }, [mapClickCoordinates, manuallyAddedFolder, routesApi, fetchLibraryWaypoints, clearMapClick])

  // Convert map click coordinates format for MapContainer
  // MapboxMapCore passes [lng, lat] array, but we need { latitude, longitude } object
  // When isDeadEndClickSearchMode is true, map click runs dead-end search within 1 mile instead of setting pin
  const handleMapClickForContainer = useCallback(async (coords: [number, number] | { latitude: number; longitude: number }) => {
    let latitude: number
    let longitude: number

    if (Array.isArray(coords)) {
      longitude = coords[0]
      latitude = coords[1]
    } else {
      latitude = coords.latitude
      longitude = coords.longitude
    }

    if (isDeadEndClickSearchMode) {
      setDeadEndUpgradeRequired(false)
      setIsLoadingDeadEnds(true)
      setDeadEnds([])
      setCandidates([])
      setDeadEndError(null)
      setDiscoverySource(null)
      setDiscoveryQueuedAt(null)
      setSearchBboxVisualization(null)
      const radiusMiles = 1
      try {
        // Use cache-first discovery: tile cache -> queue miss for background processing.
        const bbox = calculatePointBufferBbox(longitude, latitude, radiusMiles)
        const response = await routesApi.findDeadEnds(undefined, {
          coordinates: [longitude, latitude],
          radiusMiles,
          limit: 25
        })
        const allDeadEnds = [
          ...(response.deadEnds || []),
          ...(response.candidates || []).map(candidate => ({
            coordinates: candidate.coordinates,
            roadId: candidate.roadId,
            roadName: candidate.roadName,
            distanceFromMainRoad: candidate.distanceFromMainRoad,
            classification: 'dead_end' as const,
            metadata: candidate.metadata,
            candidate
          }))
        ]
        setDeadEnds(allDeadEnds)
        setCandidates(response.candidates || [])
        setDiscoverySource(response.source ?? null)
        setDiscoveryQueuedAt(response.queuedAt ?? null)
        // Build visualization for search circle display
        const [minLng, minLat, maxLng, maxLat] = bbox
        setSearchBboxVisualization(
          response.visualization ?? {
            bbox,
            bboxArea: (maxLng - minLng) * (maxLat - minLat),
            bboxSize: { width: maxLng - minLng, height: maxLat - minLat },
            bboxCenter: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
            searchCenter: [longitude, latitude],
            searchRadiusMiles: radiusMiles
          }
        )
      } catch (err: unknown) {
        const status = err && typeof err === 'object' && 'status' in err ? (err as { status: number }).status : undefined
        if (status === 403) {
          setDeadEndUpgradeRequired(true)
        }
        setDeadEnds([])
        setCandidates([])
        setSearchBboxVisualization(null)
      } finally {
        setIsLoadingDeadEnds(false)
      }
      return
    }

    handleMapClick({ latitude, longitude })
  }, [handleMapClick, isDeadEndClickSearchMode, routesApi])

  const toggleDeadEndClickSearchMode = useCallback(() => {
    setIsDeadEndClickSearchMode((prev) => {
      if (prev) {
        setDeadEnds([])
        setSearchBboxVisualization(null)
        setDeadEndUpgradeRequired(false)
      }
      return !prev
    })
  }, [])

  // Calculate total days for the selected trip
  const totalDays = useMemo(() => {
    if (!selectedTrip) return undefined
    const itineraryDays = organizeWaypointsByDay(waypoints || [], selectedTrip.start_date, selectedTrip.end_date)
    return itineraryDays.length
  }, [selectedTrip, waypoints])

  // Get panel content based on active menu
  const getPanelContent = () => {
    if (activeMenu === 'explore') {
      return (
        <ExplorePanel
          selectedWaypoint={selectedWaypoint}
          onWaypointClick={handleWaypointClick}
          onFolderWaypointsFocus={handleFolderWaypointsFocus}
          onWaypointAddedToTrip={async () => {
            await fetchLibraryWaypoints()
            setLibraryRefreshTrigger((prev) => prev + 1)
            if (selectedTrip?.id) await fetchWaypoints()
          }}
        />
      )
    }
    
    if (activeMenu === 'library') {
      return (
        <LibraryPanel
          selectedWaypoint={selectedWaypoint}
          onWaypointClick={handleWaypointClick}
          onFolderWaypointsFocus={handleFolderWaypointsFocus}
          refreshTrigger={libraryRefreshTrigger}
          onImportComplete={async () => {
            // Refresh library waypoints after import
            await fetchLibraryWaypoints()
            setLibraryRefreshTrigger(prev => prev + 1)

          }}
          onWaypointAddedToTrip={async () => {
            // Refresh library waypoints after assignment to trip
            await fetchLibraryWaypoints()
            setLibraryRefreshTrigger(prev => prev + 1)
            // Also refresh trip waypoints if a trip is selected
            if (selectedTrip?.id) {
              // The useItineraryBuilder hook will automatically refresh waypoints
              // when the trip's waypoints change, but we can trigger a manual refresh
              await fetchWaypoints()
            }

          }}
        />
      )
    }
    
    // Trips panel content
    if (viewMode === 'trips-list') {
      return (
        <TripsListPanel
          trips={trips}
          onTripSelect={handleTripSelect}
          isLoading={isLoading}
        />
      )
    } else if (selectedTrip) {
      return (
        <ItineraryView
          trip={selectedTrip}
          onBack={handleBackToTrips}
          waypoints={waypoints}
          waypointsLoading={waypointsLoading}
          onReorderWaypoints={handleReorderWaypoints}
          onDeleteWaypoint={handleDeleteWaypoint}
          onUpdateWaypointDay={handleUpdateWaypointDay}
          onUpdateWaypoint={handleUpdateWaypoint}
          onAddWaypointToDay={handleAddWaypointToDay}
          route={route}
          selectedWaypoint={selectedWaypoint}
          onWaypointSelect={handleWaypointSelectFromList}
          onOpenPOIDiscovery={() => setShowPOIDiscovery(true)}
        />
      )
    }
    return null
  }

  // Get panel title based on active menu
  const getPanelTitle = () => {
    if (activeMenu === 'explore') {
      return 'WAYPOINTS'
    }
    if (activeMenu === 'library') {
      return 'Library'
    }
    if (viewMode === 'trips-list') {
      return 'Trips'
    } else if (selectedTrip) {
      return selectedTrip.name
    }
    return 'Trips'
  }

  // Helper to detect if query looks like coordinates
  const isCoordinateQuery = useCallback((query: string): boolean => {
    const trimmed = query.trim()
    if (!trimmed) return false
    
    // Match coordinate patterns: lat,lng or lng,lat
    const coordPattern = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/
    const match = trimmed.match(coordPattern)
    
    if (!match) return false
    
    const coord1 = parseFloat(match[1])
    const coord2 = parseFloat(match[2])
    
    // Check if values are in valid coordinate ranges
    const isValidLat = coord1 >= -90 && coord1 <= 90 && Math.abs(coord2) <= 180
    const isValidLng = Math.abs(coord1) <= 180 && coord2 >= -90 && coord2 <= 90
    
    return isValidLat || isValidLng
  }, [])

  // Check if current query looks like coordinates
  const hasCoordinateQuery = useMemo(() => {
    return isCoordinateQuery(locationSearch.searchQuery) && !locationSearch.searchResult
  }, [locationSearch.searchQuery, locationSearch.searchResult, isCoordinateQuery])

  const handleRunMapLocationSearch = useCallback(
    async (rawQuery: string) => {
      const trimmed = rawQuery.trim()
      if (!trimmed) return
      setExploreSearchError(null)
      const opts = mapSearchCoordinatesOnly ? ({ coordinatesOnly: true } as const) : undefined
      const { applied } = await locationSearch.onSearch(trimmed, opts)
      if (mapSearchCoordinatesOnly && !applied) {
        setExploreSearchError(
          'Enter latitude and longitude, for example 37.8651, -119.5383.'
        )
      }
    },
    [locationSearch, mapSearchCoordinatesOnly]
  )

  // Handle paste event - auto-search coordinates if detected
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text')
      // Check if pasted text looks like coordinates
      if (isCoordinateQuery(pastedText) && !locationSearch.isSearching && !pasteHandledRef.current) {
        pasteHandledRef.current = true
        // Wait for paste to complete, then auto-search
        setTimeout(() => {
          const trimmedPasted = pastedText.trim()
          if (trimmedPasted && !locationSearch.isSearching) {
            void handleRunMapLocationSearch(trimmedPasted)
          }
          setTimeout(() => {
            pasteHandledRef.current = false
          }, 500)
        }, 50)
      }
    },
    [handleRunMapLocationSearch, isCoordinateQuery, locationSearch.isSearching]
  )

  // Mobile paste fallback: onChange fires instead of onPaste on mobile keyboards
  useEffect(() => {
    if (!hasCoordinateQuery || locationSearch.isSearching || pasteHandledRef.current) return

    const timer = setTimeout(() => {
      if (hasCoordinateQuery && !locationSearch.isSearching && !pasteHandledRef.current) {
        void handleRunMapLocationSearch(locationSearch.searchQuery)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [hasCoordinateQuery, locationSearch.searchQuery, locationSearch.isSearching, handleRunMapLocationSearch])

  // Prepare search result for display
  const searchResultForMap = useMemo(() => {
    if (locationSearch.searchResult) {
      return {
        id: 'search-result',
        name: locationSearch.searchResult.name,
        latitude: locationSearch.searchResult.latitude,
        longitude: locationSearch.searchResult.longitude,
        category: 'search',
        poi_type: 'custom',
        address: locationSearch.searchResult.address
      }
    }
    return null
  }, [locationSearch.searchResult])

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="flex-1 flex overflow-hidden min-h-0">
        {isPanelOpen && (
          <aside
            className="hidden md:flex flex-shrink-0 h-full overflow-hidden border-r border-earth-200 bg-earth-50 transition-[width] duration-200 ease-out dark:border-earth-800 dark:bg-earth-950"
            style={{ width: leftPanelCollapsed ? EXPLORE_SIDEBAR_COLLAPSED_WIDTH_PX : EXPLORE_SIDEBAR_EXPANDED_WIDTH_PX }}
            aria-label="Explore sidebar"
          >
            {leftPanelCollapsed ? (
              <div className="flex h-full w-full flex-col items-center py-3 gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setLeftPanelCollapsed(false)}
                  aria-label="Expand explore sidebar"
                >
                  <PanelRightClose className="h-5 w-5 text-earth-600 dark:text-earth-300" />
                </Button>
                <div className="flex-1 flex items-start pt-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setLeftPanelCollapsed(false)}
                    aria-label="Expand explore sidebar"
                  >
                    <PanelRightClose className="h-5 w-5 text-earth-500 dark:text-earth-400" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
                <div className="flex items-center justify-between border-b border-earth-200 dark:border-earth-800 px-4 py-3 shrink-0">
                  <h2 className="text-lg font-semibold text-earth-900 dark:text-foreground truncate">{getPanelTitle()}</h2>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setLeftPanelCollapsed(true)}
                      aria-label="Collapse explore sidebar"
                    >
                      <PanelLeftClose className="h-5 w-5 text-earth-500 dark:text-earth-400" />
                    </Button>
                    {activeMenu !== 'library' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleClosePanel}
                        aria-label="Close panel"
                      >
                        <X className="h-5 w-5 text-earth-500 dark:text-earth-400" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                  {getPanelContent()}
                </div>
              </div>
            )}
          </aside>
        )}

        <div className="flex-1 min-w-0 min-h-0 overflow-hidden relative">
          <MapContainer
            onMapClick={handleMapClickForContainer}
            mapClickCoordinates={mapClickCoordinates}
            mode="browse"
            selectedTripId={selectedTrip?.id || null}
            trip={selectedTrip}
            isItineraryBuilder={true}
            selectedWaypoint={selectedWaypoint}
            getAccessToken={getAccessToken}
            deadEnds={deadEnds}
            selectedDeadEnd={null}
            onDeadEndClick={(deadEnd) => {
              // Handle dead end click - could show details or add to itinerary
            }}
            onWaypointClick={handleWaypointClick}
            searchRouteGeometry={searchRouteGeometry}
            searchRadiusMiles={searchRadiusMiles}
            searchBboxVisualization={searchBboxVisualization}
            isDeadEndDiscoveryMode={isDeadEndClickSearchMode}
            centerOnWaypoint={centerOnWaypoint}
            centerOnWaypoints={centerOnWaypoints}
            centerOnPOI={centerOnPOI}
            centerOnDeadEnd={centerOnDeadEnd}
            showLibraryWaypoints={showLibraryWaypoints}
            showPublicLands={false}
            showDispersedCampsites={showDispersedCampsites}
            onDispersedCampsitesLoadingChange={setDispersedCampsitesLoading}
            onDispersedCampsitesUpgradeRequired={() => {
              setCampFinderUpgradeRequired(true)
              setShowDispersedCampsites(false)
            }}
            onForestRoadsError={setForestRoadsError}
            centerOnUserLocation={
              userLocation.location || userLocation.approximateLocation
                ? {
                    latitude: userLocation.location?.latitude ?? userLocation.approximateLocation?.latitude ?? 0,
                    longitude: userLocation.location?.longitude ?? userLocation.approximateLocation?.longitude ?? 0
                  }
                : null
            }
            centerOnSearch={locationSearch.centerOnSearch}
            searchResult={searchResultForMap}
            userLocation={userLocation}
            poiResults={poiHandlers.poiResults}
            onPOIClick={handlePOIClick}
          />

          {/* Map controls: location search on all /explore/*; Find dead ends (Explore + Trips); library toggle when not in library panel */}
          <div className="absolute top-4 left-4 z-10 flex flex-col gap-3">
            {showMapLocationSearch && (
              <div className="w-64 sm:w-80 relative">
                <div className="bg-card rounded-lg shadow-md border border-border p-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={locationSearch.searchQuery}
                      onChange={(e) => {
                        locationSearch.setSearchQuery(e.target.value)
                        setExploreSearchError(null)
                      }}
                      onPaste={handlePaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !locationSearch.isSearching) {
                          const trimmedQuery = locationSearch.searchQuery.trim()
                          if (trimmedQuery) {
                            void handleRunMapLocationSearch(trimmedQuery)
                          }
                        }
                      }}
                      placeholder={
                        mapSearchCoordinatesOnly
                          ? 'Latitude, longitude (e.g. 37.8651, -119.5383)'
                          : 'Search for a location...'
                      }
                      className={`w-full pl-8 pr-8 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 bg-background ${
                        hasCoordinateQuery
                          ? 'border-adventure-500 focus:ring-adventure-500 focus:border-adventure-500 bg-adventure-50 dark:bg-adventure-950/30'
                          : 'border-border focus:ring-adventure-500 focus:border-adventure-500'
                      }`}
                      disabled={locationSearch.isSearching}
                    />
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    {locationSearch.searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          locationSearch.setSearchQuery('')
                          setExploreSearchError(null)
                        }}
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {hasCoordinateQuery && (
                    <div className="mt-2 px-2 py-1.5 bg-adventure-100 dark:bg-adventure-900/40 border border-adventure-300 dark:border-adventure-700 rounded text-xs text-adventure-800 dark:text-adventure-100 flex items-center gap-2">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span>
                        Coordinates detected.{' '}
                        {locationSearch.isSearching
                          ? 'Searching...'
                          : 'Press Enter to search or paste to auto-search'}
                      </span>
                    </div>
                  )}
                </div>

                {exploreSearchError && mapSearchCoordinatesOnly && (
                  <p
                    role="alert"
                    className="mt-2 px-2 py-1.5 text-xs text-destructive bg-destructive/10 rounded-md border border-destructive/20"
                  >
                    {exploreSearchError}
                  </p>
                )}

                {locationSearch.searchResult && !mapSearchCoordinatesOnly && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card rounded-md shadow-lg border border-border p-3 z-50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {locationSearch.searchResult.name}
                        </p>
                        {locationSearch.searchResult.address && (
                          <p className="text-xs text-muted-foreground truncate">
                            {locationSearch.searchResult.address}
                          </p>
                        )}
                      </div>
                      {locationSearch.onClearResult && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            locationSearch.onClearResult()
                            setExploreSearchError(null)
                          }}
                          className="flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {handleAddSearchResultToTimeline && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleAddSearchResultToTimeline}
                          className="flex-1"
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Add to Timeline
                        </Button>
                      )}
                      {handleSaveSearchResultAsWaypoint && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSaveSearchResultAsWaypoint}
                          className={handleAddSearchResultToTimeline != null ? 'flex-1' : 'w-full'}
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Add to Library
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dispersed camp finder: explore page only */}
            {isExploreRoute && (
              <div className="flex flex-col gap-2">
                <DispersedCampFinderToggle
                  showCampsites={showDispersedCampsites}
                  isLoading={dispersedCampsitesLoading}
                  onToggle={(next) => {
                    setCampFinderUpgradeRequired(false)
                    if (next && !canUseCampFinder) {
                      setCampFinderUpgradeRequired(true)
                      return
                    }
                    setShowDispersedCampsites(next)
                  }}
                />
                {showDispersedCampsites && (
                  <p className="text-xs text-earth-600 dark:text-earth-300 bg-card/95 px-2 py-1.5 rounded border border-border shadow max-w-xs">
                    Zoom in to reveal individual campsites.
                  </p>
                )}
                {campFinderUpgradeRequired && (
                  <div className="bg-card rounded-lg shadow-md border border-border p-2 max-w-xs">
                    <UpgradePrompt
                      feature="Dispersed Camp Finder"
                      message="Upgrade to Pro to discover dispersed campsites on the map."
                    />
                  </div>
                )}
              </div>
            )}

            {activeMenu !== 'library' && (
              <LibraryWaypointsToggle
                showLibraryWaypoints={showLibraryWaypoints}
                onToggle={setShowLibraryWaypoints}
              />
            )}
            </div>

          {/* Route Loading Indicator */}
          {routesLoading && (
            <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-2" style={{ marginTop: '48px' }}>
              <div className="text-sm text-gray-700">Loading routes...</div>
            </div>
          )}
        </div>
      </div>

      {/* Waypoint Details Panel - Shows for both waypoint clicks and map clicks. */}
      {/* Intentionally limited to itinerary-builder trips view: do not use on /explore or when library/explore panel is active (map centering only there). */}
      {/* Hide when POI Discovery Panel is open, or when library or explore panel is active. */}
      {(selectedWaypoint || mapClickCoordinates) && !showPOIDiscovery && activeMenu !== 'explore' && activeMenu !== 'library' && (
        <WaypointDetailsPanel
          waypoint={selectedWaypoint}
          coordinates={!selectedWaypoint ? mapClickCoordinates : undefined}
          tripId={selectedTrip?.id}
          onClose={() => {
            if (selectedWaypoint) {
              handleCloseWaypointDetails()
            } else {
              clearMapClick()
            }
          }}
          onAddStop={selectedTrip && !selectedWaypoint ? handleAddStopClick : undefined}
          isAddingStop={isAddingStop}
          onUpdateWaypoint={selectedWaypoint ? handleUpdateWaypoint : undefined}
          onUpdateWaypointDay={selectedWaypoint ? handleUpdateWaypointDay : undefined}
          totalDays={totalDays}
          isLibraryView={false}
          onAddToLibrary={undefined}
        />
      )}

      {isExploreAppRoute &&
        !selectedTrip &&
        locationSearch.searchResult &&
        !showPOIDiscovery && (
        <WaypointDetailsPanel
          panelVariant="floating"
          coordinates={{
            latitude: locationSearch.searchResult.latitude,
            longitude: locationSearch.searchResult.longitude
          }}
          locationDisplayName={locationSearch.searchResult.name}
          onClose={() => {
            locationSearch.onClearResult()
            locationSearch.setSearchQuery('')
            setExploreSearchError(null)
          }}
          isLibraryView={false}
        />
      )}

      {/* Campsite Search Dialog */}
      {selectedTrip && routeSegments && routeSegments.length > 0 && (
        <CampsiteSearchDialog
          open={showCampsiteDialog}
          onOpenChange={setShowCampsiteDialog}
          routeSegments={routeSegments}
          waypoints={waypoints}
          tripId={selectedTrip.id}
          tripName={selectedTrip.name}
          onSearch={handleDialogSearch}
          isLoading={isLoadingDeadEnds}
        />
      )}

      {/* POI Discovery Panel - Right side panel when trips menu is active and trip is selected */}
      {activeMenu === 'trips' && selectedTrip && showPOIDiscovery && (
        <POIDiscoveryPanel
          tripId={selectedTrip.id}
          trip={selectedTrip}
          poiResults={poiHandlers.poiResults}
          isPOISearching={poiHandlers.isPOISearching}
          poiSearchError={poiHandlers.poiSearchError}
          onPOISearch={(params) => poiHandlers.handlePOISearch({
            ...params,
            tripId: effectiveTripId || undefined,
            tripDay: effectiveTripDay
          })}
          onClearPOIResults={poiHandlers.handleClearPOIResults}
          onPOIClick={handlePOIClick}
          onAddPOIToTrip={handleAddPOIToTrip}
          onClose={() => {
            setShowPOIDiscovery(false)
          }}
          waypoints={waypoints || []}
          userLocation={userLocation.location || userLocation.approximateLocation || null}
          routeId={effectiveRouteId || undefined}
          routeName={route?.name || (routeSegments && routeSegments.length > 0 ? `${selectedTrip?.name} - Route` : undefined)}
          deadEnds={deadEnds}
          isLoadingDeadEnds={isLoadingDeadEnds}
          deadEndError={deadEndError}
          discoverySource={discoverySource}
          discoveryQueuedAt={discoveryQueuedAt}
          onFindCampsites={handleFindCampsites}
          onAddDeadEndToTrip={handleAddDeadEndToTrip}
          onDeadEndClick={(deadEnd) => {
            // Center map on dead end
            if (deadEnd.coordinates && deadEnd.coordinates.length === 2) {
              const [lng, lat] = deadEnd.coordinates
              setCenterOnDeadEnd({ latitude: lat, longitude: lng })
            }
          }}
        />
      )}

      {/* Add Waypoint to Trip Panel */}
      {selectedWaypointForAdd && (
        <AddWaypointToTripPanel
          waypoint={selectedWaypointForAdd}
          onClose={() => {
            setSelectedWaypointForAdd(null)
            setSelectedWaypoint(null)
          }}
          onSuccess={async () => {
            // Refresh library waypoints after assignment to trip
            await fetchLibraryWaypoints()
            
            // Refresh trip waypoints if viewing that trip
            // Routes will regenerate automatically via useClientSideRoutes hook
            if (selectedTrip?.id && fetchWaypoints) {

              try {
                // Refresh waypoints - routes will regenerate automatically
                await fetchWaypoints()
                

              } catch (error) {

              }
            } else {

            }
            
            setSelectedWaypointForAdd(null)
            setSelectedWaypoint(null)
          }}
        />
      )}
    </div>
  )
}

export default function ItineraryBuilderPage() {
  return (
    <PageLayout>
      <MapDataProvider>
        <MapPageProvider initialMode="browse">
          <ItineraryBuilderPageContent />
        </MapPageProvider>
      </MapDataProvider>
    </PageLayout>
  )
}
